import { ref, readonly, type Ref } from "vue";
import { useToast } from "primevue/usetoast";
import { restoreUser } from "../api";

interface UseUnarchiveUserReturn {
  readonly isUnarchiving: Ref<boolean>;
  unarchiveUser: (userId: number) => Promise<boolean>;
}

/**
 * Composable for unarchiving (restoring) a user
 */
export function useUnarchiveUser(): UseUnarchiveUserReturn {
  const toast = useToast();
  const isUnarchiving = ref(false);

  async function unarchiveUser(userId: number): Promise<boolean> {
    if (!userId) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "User ID is required",
        life: 5000,
      });
      return false;
    }

    isUnarchiving.value = true;

    try {
      const result = await restoreUser(userId);

      // Check for error (result could be undefined/null for success responses)
      if (result && typeof result === "object" && "error" in result) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.error || "Failed to restore user",
          life: 5000,
        });
        return false;
      }

      // If we get here, the restore was successful
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "User has been restored successfully",
        life: 3000,
      });

      return true;
    } catch (error) {
      console.error("Error restoring user:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "An unexpected error occurred",
        life: 5000,
      });
      return false;
    } finally {
      isUnarchiving.value = false;
    }
  }

  return {
    isUnarchiving: readonly(isUnarchiving),
    unarchiveUser,
  };
}
